import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";

interface BLDTableSkeletonProps {
  /** Number of placeholder columns - keep it aligned with the section's headCells */
  columns?: number;
  rows?: number;
  /** The alert + download row that sits above most section tables */
  showToolbar?: boolean;
}

const SectionToolbarSkeleton = () => (
  <Stack
    flexDirection={"row"}
    alignItems="center"
    gap={2}
    margin="4px 16px"
    data-testid="bld-section-toolbar-skeleton"
  >
    <Skeleton
      variant="rounded"
      height={48}
      sx={{ width: "100%", borderRadius: "4px" }}
    />
    <Skeleton
      variant="circular"
      width={28}
      height={28}
      sx={{ flexShrink: 0, mr: 1 }}
    />
  </Stack>
);

/**
 * Generic table placeholder used by every Business Leader Dashboard section
 * while its own API call is in flight.
 */
export const BLDTableSkeleton = ({
  columns = 6,
  rows = 6,
  showToolbar = true,
}: BLDTableSkeletonProps) => (
  <>
    {showToolbar && <SectionToolbarSkeleton />}
    <TableContainer
      className="RunRateTableWrap"
      component={Paper}
      sx={{
        boxShadow: "none",
        maxHeight: 400,
        overflowY: "auto",
        overflowX: "auto",
      }}
      data-testid="bld-table-skeleton"
    >
      <Table aria-label="loading table" size="small">
        <TableHead>
          <TableRow>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <TableCell key={`bld-skeleton-head-${colIndex}`} align="left">
                <Skeleton variant="text" width={72} height={20} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={`bld-skeleton-row-${rowIndex}`}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell
                  key={`bld-skeleton-cell-${rowIndex}-${colIndex}`}
                  align="left"
                >
                  <Skeleton
                    variant="text"
                    width={colIndex === 0 ? 96 : 56}
                    height={18}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

/**
 * Placeholder for the High Level KPI gauges + the Run Rate table underneath.
 * Both are fed by the same query, so they share one loading state.
 */
export const HighLevelKpiSkeleton = () => (
  <>
    <Box sx={{ padding: "8px 16px 0px", mb: 2, boxShadow: "none" }}>
      <Skeleton variant="text" width={220} height={40} sx={{ mb: 2 }} />
      <Grid2 container mt={0} columnSpacing={6} mb={1}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Grid2 size={4} key={`bld-kpi-skeleton-${index}`}>
            <Paper sx={{ height: "210px", padding: "8px 16px" }}>
              <Stack
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems="center"
              >
                <Skeleton variant="text" width={60} height={28} />
                <Skeleton variant="circular" width={24} height={24} />
              </Stack>
              <Stack alignItems="center" justifyContent="center">
                <Skeleton variant="circular" width={150} height={150} />
              </Stack>
            </Paper>
          </Grid2>
        ))}
      </Grid2>
    </Box>

    <Stack
      sx={{ padding: "8px 16px" }}
      flexDirection={"row"}
      alignItems="center"
      justifyContent="space-between"
    >
      <Skeleton variant="text" width={300} height={40} />
      <Skeleton variant="rounded" width={220} height={28} />
    </Stack>

    <BLDTableSkeleton columns={6} rows={5} />
  </>
);
